import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { useRestQuery } from "../../../hooks/useRestQuery";
import { listClassrooms } from "../../../api/classrooms";
import { useObservations } from "../hooks/useObservations";
import ObservationForm from "./ObservationForm";
import { useTranslationsContext } from "../../../contexts/TranslationsContext";
import type { ClassRoomGroup } from "../../../api/types";
import type { CreateObservationPayload } from "../types";
import { variantSuccess } from "../../../constants/constants";

import type { ObservationSource } from "../types";

interface QuickObservationModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the form with student/class context (used by ParticiFy integration) */
  prefill?: {
    StudentId?: number;
    ClassRoomId?: number;
    CalendarEventId?: number;
    text?: string;
    date?: string;
  };
  /** Source module — defaults to LOGIFY */
  source?: ObservationSource;
  /** Called after an observation is successfully created */
  onSuccess?: () => void;
}

export default function QuickObservationModal({ open, onClose, prefill, source = "LOGIFY", onSuccess }: QuickObservationModalProps) {
  const { translation } = useTranslationsContext();
  const { enqueueSnackbar } = useSnackbar();

  const { data: classRoomGroups } = useRestQuery<ClassRoomGroup[]>(
    () => listClassrooms(),
    { key: "logify-modal-classrooms", skip: !open }
  );
  const classRooms = classRoomGroups?.flatMap((g) => g.classRooms) ?? [];

  const { createObservation, creating } = useObservations({}, { skipFetch: true });

  const handleSubmit = async (payload: CreateObservationPayload) => {
    const result = await createObservation({
      ...payload,
      source,
      ...(prefill?.CalendarEventId && { CalendarEventId: prefill.CalendarEventId }),
    });
    if (result) {
      enqueueSnackbar(translation.LOGIFY_CREATED?.defaultMessage || "Observation saved", { variant: variantSuccess });
      onSuccess?.();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: "rgba(59, 55, 88, 0.92)",
          backdropFilter: "blur(24px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: "rgba(255,255,255,0.9)" }}>
          {translation.LOGIFY_QUICK_BUTTON?.defaultMessage || "Observation"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <ObservationForm
          classRooms={classRooms}
          initialData={prefill}
          onSubmit={handleSubmit}
          onCancel={onClose}
          loading={creating}
        />
      </DialogContent>
    </Dialog>
  );
}
